// src/utils/taskColumnDefs.ts
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { TaskData } from "./taskTypes"; // create and import the type
// Parse HTML in an inert document: unlike a div in the live page, it never
// fetches/decodes <img> sources, which made grid formatting very slow.
const inertDoc = document.implementation.createHTMLDocument("");
const MAX_CACHE = 20000;
const textCache = new Map<string, string>();
const imgOrTextCache = new Map<string, string>();
const remember = (cache: Map<string, string>, key: string, value: string) => {
  if (cache.size >= MAX_CACHE) cache.clear();
  cache.set(key, value);
  return value;
};

function stripHtml(html: string): string {
  if (!html) return "";
  const cached = textCache.get(html);
  if (cached !== undefined) return cached;
  if (!/[<&]/.test(html)) return html; // plain text, nothing to strip
  const div = inertDoc.createElement("div");
  div.innerHTML = html;
  return remember(textCache, html, div.textContent || "");
}
const isAdmin = localStorage.getItem("isAdmin") === "true";
const isSuperAdmin = localStorage.getItem("superadmin") === "true";

const extractImageSrcOrText = (html: string): string => {
  if (!html) return "";
  const cached = imgOrTextCache.get(html);
  if (cached !== undefined) return cached;

  const div = inertDoc.createElement("div");
  div.innerHTML = html;

  const src = div.querySelector("img")?.getAttribute("src");
  if (src) {
    // Return the image URL (resolved like img.src would be)
    let url = src;
    try {
      url = new URL(src, document.baseURI).href;
    } catch {}
    return remember(imgOrTextCache, html, url);
  }

  // Else return plain text
  return remember(imgOrTextCache, html, div.textContent || "");
};

// Show the server's date/time text as it is (like the task modal), with no
// time zone conversion: "2026-10-12T23:00:00" -> "12/10/2026, 23:00:00"
const formatDateUTC3 = (raw: string | null | undefined): string => {
  if (!raw) return "";

  const m = String(raw).match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (!m) return String(raw);

  const [, year, month, day, hours, minutes, seconds = "00"] = m;
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};
const getColumnDefs = (
  handleEdit: (data: TaskData) => void,
  handleDelete: (ticketId: string) => void
): ColDef<TaskData>[] => [
  {
    field: "ticketId",
    headerName: "Ticket ID",
    filter: "agTextColumnFilter",
    pinned: "left",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "status",
    headerName: "Status",
    filter: "agMultiColumnFilter",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  { field: "phase", headerName: "Phase" },

  {
    field: "customerapproval",
    headerName: "Customer Approval",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "tasktype",
    headerName: "Type Of Task",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "affectedCustomer",
    headerName: "Customer",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "affectedNetwork",
    headerName: "Network",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "affectedSite",
    headerName: "Site",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "affectedLink",
    headerName: "Link",
    filterParams: {
      // Ensure all values are available when restoring
      suppressMiniFilter: false,
      apply: true,
    },
  },
  {
    field: "affectedAntenna",
    headerName: "Antenna",
  },
  { field: "asset", headerName: "Asset" },
  {
    field: "taskStartDatetime",
    headerName: "Planned Start Date/Time",
    filter: "agDateColumnFilter",
    valueGetter: (params: any) => {
      const raw = params.data?.taskStartDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    filterValueGetter: (params: any) => {
      const raw = params.data?.taskStartDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    valueFormatter: (params: any) =>
      formatDateUTC3(params.data?.[params.colDef.field]),

    filterParams: {
      browserDatePicker: true,
      comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
        if (!cellValue) return -1;

        // Strip time from both values
        const cellDate = new Date(
          cellValue.getFullYear(),
          cellValue.getMonth(),
          cellValue.getDate()
        );

        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0; // dates are equal
      },
    },
  },
  {
    field: "taskCloseDatetime",
    headerName: "Planned End Date/Time",
    filter: "agDateColumnFilter",
    cellDataType: "dateTime",
    valueGetter: (params: any) => {
      const raw = params.data?.taskCloseDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    filterValueGetter: (params: any) => {
      const raw = params.data?.taskCloseDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    valueFormatter: (params: any) =>
      formatDateUTC3(params.data?.[params.colDef.field]),

    filterParams: {
      browserDatePicker: true,
      comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
        if (!cellValue) return -1;

        // Strip time from both values
        const cellDate = new Date(
          cellValue.getFullYear(),
          cellValue.getMonth(),
          cellValue.getDate()
        );

        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0; // dates are equal
      },
    },
  },
  {
    field: "summary",
    headerName: "Summary",
    valueGetter: (params: ValueGetterParams<TaskData>) =>
      stripHtml(params.data?.summary || ""),
    filter: "agTextColumnFilter",
    valueFormatter: (params: { value: string }) => stripHtml(params.value),
  },
  {
    field: "description",
    headerName: "Description",
    valueGetter: (params: ValueGetterParams<TaskData>) =>
      stripHtml(params.data?.description || ""),
    filter: "agTextColumnFilter",
    valueFormatter: (params: { value: string }) => stripHtml(params.value),
  },
  {
    field: "solution",
    headerName: "Solution",
    valueGetter: (params: ValueGetterParams<TaskData>) =>
      extractImageSrcOrText(params.data?.solution || ""),
    filter: "agTextColumnFilter",
    valueFormatter: (params: { value: string }) => stripHtml(params.value),
  },
  {
    field: "imapcttaskStartDatetime",
    headerName: "Impact Start Date/time",
    filter: "agDateColumnFilter",
    valueGetter: (params: any) => {
      const raw = params.data?.imapcttaskStartDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    filterValueGetter: (params: any) => {
      const raw = params.data?.imapcttaskStartDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    valueFormatter: (params: any) =>
      formatDateUTC3(params.data?.[params.colDef.field]),

    filterParams: {
      browserDatePicker: true,
      comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
        if (!cellValue) return -1;

        // Strip time from both values
        const cellDate = new Date(
          cellValue.getFullYear(),
          cellValue.getMonth(),
          cellValue.getDate()
        );

        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0; // dates are equal
      },
    },
  },
  {
    field: "impacttaskCloseDatetime",
    headerName: "Impact Close Date/time",
    filter: "agDateColumnFilter",
    valueGetter: (params: any) => {
      const raw = params.data?.impacttaskCloseDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    filterValueGetter: (params: any) => {
      const raw = params.data?.impacttaskCloseDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    valueFormatter: (params: any) =>
      formatDateUTC3(params.data?.[params.colDef.field]),

    filterParams: {
      browserDatePicker: true,
      comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
        if (!cellValue) return -1;

        // Strip time from both values
        const cellDate = new Date(
          cellValue.getFullYear(),
          cellValue.getMonth(),
          cellValue.getDate()
        );

        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0; // dates are equal
      },
    },
  },
  {
    field: "taskResolutionTime",
    headerName: "Impact Period",
    filter: "agNumberColumnFilter",
  },
  { field: "responsiblePerson", headerName: "Responsible Person" },
  { field: "responsibleTeam", headerName: "Responsible Team" },
  {
    field: "username",
    headerName: "User",
    filter: "agTextColumnFilter",
  },
  {
    field: "createdby",
    headerName: "Created By",
  },
  {
    field: "closedby",
    headerName: "Closed By",
  },
  { field: "priority", headerName: "Priority" },
  { field: "closureCode", headerName: "Closure Code" },

  {
    field: "ticketCreationDatetime",
    headerName: "Ticket Creation Datetime",
    filter: "agDateColumnFilter",
    valueGetter: (params: any) => {
      const raw = params.data?.ticketCreationDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    filterValueGetter: (params: any) => {
      const raw = params.data?.ticketCreationDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    valueFormatter: (params: any) =>
      formatDateUTC3(params.data?.[params.colDef.field]),

    filterParams: {
      browserDatePicker: true,
      comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
        if (!cellValue) return -1;

        // Strip time from both values
        const cellDate = new Date(
          cellValue.getFullYear(),
          cellValue.getMonth(),
          cellValue.getDate()
        );

        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0; // dates are equal
      },
    },
    sort: "desc",
    sortIndex: 1,
  },
  {
    field: "ticketClosureDatetime",
    headerName: "Ticket Closure Datetime",
    filter: "agDateColumnFilter",
    valueGetter: (params: any) => {
      const raw = params.data?.ticketClosureDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    filterValueGetter: (params: any) => {
      const raw = params.data?.ticketClosureDatetime;
      return raw ? new Date(raw.replace(" ", "T")) : null;
    },
    valueFormatter: (params: any) =>
      formatDateUTC3(params.data?.[params.colDef.field]),

    filterParams: {
      browserDatePicker: true,
      comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
        if (!cellValue) return -1;

        // Strip time from both values
        const cellDate = new Date(
          cellValue.getFullYear(),
          cellValue.getMonth(),
          cellValue.getDate()
        );

        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0; // dates are equal
      },
    },
  },

  { field: "relatedobject", headerName: "Related Object" },

  {
    colId: "actions",
    headerName: "Actions",
    cellRenderer: (params: any) => (
      <div style={{ display: "flex", gap: "8px" }}>
        <button style={{}} onClick={() => handleEdit(params.data)}>
          Edit
        </button>
        {isSuperAdmin && (
          <button onClick={() => handleDelete(params.data.ticketId)}>
            Delete
          </button>
        )}
      </div>
    ),
    pinned: "right",
    flex: 1,
  },
];

export default getColumnDefs;
